import { useEffect, useState, useCallback } from "react";
import { API_BASE } from "@/lib/api";
import { X, ChevronLeft, ChevronRight, FileText, Download, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Full-screen attachment preview overlay with swipe / arrow navigation
 * and tap-to-zoom (2x) for photos. Falls back to a download link for PDFs.
 *
 * Props:
 *   items: [{id, path, filename, kind, content_type}]
 *   startIndex: number
 *   onClose: () => void
 */
export default function AttachmentPreview({ items = [], startIndex = 0, onClose }) {
  const [i, setI] = useState(startIndex);
  const [zoom, setZoom] = useState(1);

  useEffect(() => { setI(startIndex); setZoom(1); }, [startIndex]);

  const next = useCallback(() => { setZoom(1); setI((v) => (v + 1) % items.length); }, [items.length]);
  const prev = useCallback(() => { setZoom(1); setI((v) => (v - 1 + items.length) % items.length); }, [items.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  // Touch swipe
  const [touch, setTouch] = useState(null);
  const onTouchStart = (e) => setTouch({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  const onTouchEnd = (e) => {
    if (!touch) return;
    const dx = e.changedTouches[0].clientX - touch.x;
    const dy = e.changedTouches[0].clientY - touch.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      dx < 0 ? next() : prev();
    } else if (dy < -80 && Math.abs(dy) > Math.abs(dx)) {
      onClose?.();
    }
    setTouch(null);
  };

  if (!items.length) return null;
  const item = items[i];
  const url = `${API_BASE}/api/files/${item.path}`;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0B0B0B]/97 backdrop-blur-sm flex flex-col"
      data-testid="attachment-preview"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2 text-white/90">
        <div className="text-sm min-w-0">
          <div className="font-heading font-bold truncate">{item.filename || "Attachment"}</div>
          <div className="text-[11px] text-white/50">{i + 1} of {items.length}</div>
        </div>
        <div className="flex items-center gap-2">
          {item.kind === "photo" && (
            <>
              <button
                type="button"
                data-testid="preview-zoom-out"
                onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(2)))}
                disabled={zoom <= 1}
                className="w-10 h-10 rounded-full bi-ghost-btn flex items-center justify-center disabled:opacity-40"
              ><ZoomOut className="w-4 h-4" /></button>
              <button
                type="button"
                data-testid="preview-zoom-in"
                onClick={() => setZoom((z) => Math.min(4, +(z + 0.5).toFixed(2)))}
                disabled={zoom >= 4}
                className="w-10 h-10 rounded-full bi-ghost-btn flex items-center justify-center disabled:opacity-40"
              ><ZoomIn className="w-4 h-4" /></button>
            </>
          )}
          <a
            href={url}
            download={item.filename}
            data-testid="preview-download"
            className="w-10 h-10 rounded-full bi-ghost-btn flex items-center justify-center"
            target="_blank" rel="noreferrer"
          ><Download className="w-4 h-4" /></a>
          <button
            type="button"
            data-testid="preview-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          ><X className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Image / doc */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden select-none">
        {items.length > 1 && (
          <button
            type="button"
            data-testid="preview-prev"
            onClick={prev}
            className="hidden md:flex absolute left-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center text-white z-10"
          ><ChevronLeft className="w-6 h-6" /></button>
        )}
        {item.kind === "photo" ? (
          <img
            src={url}
            alt={item.filename}
            onDoubleClick={() => setZoom((z) => (z >= 2 ? 1 : 2))}
            style={{ transform: `scale(${zoom})`, transition: "transform 200ms ease-out" }}
            className="max-h-full max-w-full object-contain"
            data-testid="preview-image"
          />
        ) : (
          <div className="text-center p-8" data-testid="preview-pdf">
            <div className="mx-auto w-20 h-20 rounded-2xl bg-[#FF9500]/15 text-[#FF9500] flex items-center justify-center mb-4">
              <FileText className="w-10 h-10" />
            </div>
            <div className="text-white/80 mb-4 font-heading font-bold text-lg">{item.filename}</div>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 h-12 px-5 rounded-xl bi-accent-btn"
            ><Download className="w-4 h-4" /> Open PDF</a>
          </div>
        )}
        {items.length > 1 && (
          <button
            type="button"
            data-testid="preview-next"
            onClick={next}
            className="hidden md:flex absolute right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center text-white z-10"
          ><ChevronRight className="w-6 h-6" /></button>
        )}
      </div>

      {/* Thumbnail rail */}
      {items.length > 1 && (
        <div className="px-4 py-3 flex gap-2 overflow-x-auto">
          {items.map((it, idx) => (
            <button
              key={it.id}
              type="button"
              onClick={() => { setI(idx); setZoom(1); }}
              data-testid={`preview-thumb-${idx}`}
              className={`w-14 h-14 rounded-lg overflow-hidden border shrink-0 transition ${idx === i ? "border-[#FF9500]" : "border-white/10 opacity-60 hover:opacity-100"}`}
            >
              {it.kind === "photo" ? (
                <img src={`${API_BASE}/api/files/${it.path}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-white/5 text-[#FF9500] flex items-center justify-center"><FileText className="w-5 h-5" /></div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
